import useTabStore from "@/stores/useTabStore";
import { packLayouts } from "@/onboarding/templates/packLayout";
import type { WidgetFilterData } from "@/utils/DashboardLayouts";
import type { DemoTemplate } from "./templates";

export const DEMO_TAB_TITLE = "Newsquawk Demo";

/**
 * Swaps the demo tab's contents for the chosen desk's template.
 *
 * Deliberately not onboarding's applyLayoutData, for two reasons.
 *
 * It calls importTab, which ADDS a tab — fine when a trialist applies one
 * template once, wrong here, where changing your mind about which desk you
 * trade would leave a trail of abandoned tabs.
 *
 * And it access-filters inaccessible widgets out. The macro template wants the
 * opposite: our EconomicCalendar is deliberately included while locked,
 * because it is the reason a macro trader is looking, and WidgetContainer
 * already renders it as the premium placeholder. One locked tile among four
 * live ones is the paywall beat; dropping it would remove the only honest
 * reason to upgrade from that screen.
 *
 * Widget ids are used verbatim rather than remapped to random composites, so
 * the guided run can ring `template.filteredWireId` without threading a
 * generated id back out. Safe because we replace the tab wholesale — there is
 * nothing left to collide with.
 */
export function applyDemoTemplate(template: DemoTemplate): void {
  const layout = packLayouts(template.widgets);

  const widgetFilters: Record<string, WidgetFilterData> = {};
  for (const spec of template.widgets) {
    if (!spec.filter && !spec.highlight) continue;
    widgetFilters[spec.id] = {
      ...(spec.filter && { filter: spec.filter }),
      ...(spec.highlight && { highlight: spec.highlight }),
    };
  }

  const enabledWidgets = template.widgets.map((w) => w.id);

  useTabStore.setState((state) => {
    const targetId = state.activeTabId ?? state.tabs[0]?.id;
    return {
      tabs: state.tabs.map((tab) =>
        tab.id === targetId
          ? {
              ...tab,
              title: DEMO_TAB_TITLE,
              enabledWidgets,
              layout,
              widgetFilters,
            }
          : tab
      ),
    };
  });
}
